import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DevicePhoneMobileIcon, TrashIcon, PencilSquareIcon } from '@heroicons/react/24/outline';

import {
  Button,
  Card,
  CardHeader,
  ContentLoader,
  EmptyState,
  Input,
  Modal,
  ModalFooter,
  Pagination,
} from '@/components/common';
import { getErrorMessage } from '@/services';
import { pospContactsApi, type SyncedContact } from '@/services/pospPortal';
import { formatDateTime } from '@/utils';

/**
 * P4 — "Contact Sync": the agent's own phone contacts, uploaded by the app.
 *
 * This card is the agent's view of data that only the agent has. There is no
 * broker-side counterpart and there is not going to be one: the whole point of
 * the feature is that an agent's address book is theirs. The server enforces
 * that (`apps.posp_portal.views.SyncedContactViewSet` scopes every read to the
 * caller's own agent row and the model has no broker column at all); this card
 * simply has no way to ask for anyone else's.
 *
 * The UPLOAD happens in the mobile app, against
 * `POST /posp-portal/contacts/sync/`. There is no browser equivalent because a
 * browser has no address book to read — so this screen deliberately explains
 * where the data comes from rather than offering a button that cannot work.
 */

const PAGE_SIZE = 10;

export function PospContactSyncCard() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<SyncedContact | null>(null);
  const [draftName, setDraftName] = useState('');
  const [draftNotes, setDraftNotes] = useState('');
  const [actionError, setActionError] = useState('');

  const listQuery = useQuery({
    queryKey: ['posp-synced-contacts', page, search],
    queryFn: () => pospContactsApi.list({ page, page_size: PAGE_SIZE, search: search || undefined }),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['posp-synced-contacts'] });

  const saveMutation = useMutation({
    mutationFn: ({ id, name, notes }: { id: string; name: string; notes: string }) =>
      pospContactsApi.update(id, { name, notes }),
    onSuccess: () => {
      setEditing(null);
      setActionError('');
      // Refetch rather than trusting the response body: the list on screen has
      // to be what the server actually stored.
      invalidate();
    },
    onError: (err) => setActionError(getErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => pospContactsApi.remove(id),
    onSuccess: () => {
      setActionError('');
      invalidate();
    },
    onError: (err) => setActionError(getErrorMessage(err)),
  });

  function openEditor(contact: SyncedContact) {
    setEditing(contact);
    setDraftName(contact.name);
    setDraftNotes(contact.notes);
  }

  const contacts = listQuery.data?.results ?? [];
  const totalPages = listQuery.data?.total_pages ?? 1;

  /** Loading, error and empty are three distinct states — a failed fetch must
   *  never render as "you have no contacts", which would read as authoritative
   *  and be wrong. */
  function renderBody() {
    if (listQuery.isLoading) {
      return <ContentLoader />;
    }

    if (listQuery.isError) {
      return (
        <div className="text-center py-10">
          <p className="text-sm font-medium text-gray-900">Your contacts could not be loaded</p>
          <p className="mt-1 text-sm text-gray-500">{getErrorMessage(listQuery.error)}</p>
          <Button variant="outline" size="sm" className="mt-4" onClick={() => listQuery.refetch()}>
            Try again
          </Button>
        </div>
      );
    }

    if (contacts.length === 0) {
      return (
        <EmptyState
          icon={<DevicePhoneMobileIcon />}
          title={search ? 'No contact matches that search' : 'No contacts synced yet'}
          description={
            search
              ? 'Try a different name or number.'
              : 'Contacts appear here once you sync them from the agent app on your phone. They stay private to you.'
          }
        />
      );
    }

    return (
      <>
        <ul className="divide-y divide-gray-100">
          {contacts.map((contact) => (
            <li key={contact.id} className="py-3 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {contact.name || 'Unnamed contact'}
                </p>
                <p className="text-sm text-gray-500">{contact.phone}</p>
                {contact.notes && (
                  <p className="text-xs text-gray-400 mt-1 break-words">{contact.notes}</p>
                )}
                <p className="text-xs text-gray-400 mt-1">
                  Last synced {formatDateTime(contact.last_synced_at)}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Edit ${contact.name || contact.phone}`}
                  onClick={() => openEditor(contact)}
                >
                  <PencilSquareIcon className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Remove ${contact.name || contact.phone}`}
                  isLoading={deleteMutation.isPending && deleteMutation.variables === contact.id}
                  onClick={() => deleteMutation.mutate(contact.id)}
                >
                  <TrashIcon className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
        {totalPages > 1 && (
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={listQuery.data?.count ?? 0}
            itemsPerPage={PAGE_SIZE}
            onPageChange={setPage}
          />
        )}
      </>
    );
  }

  return (
    <Card>
      <CardHeader
        title="Contact Sync"
        subtitle="Contacts you upload from the agent app. Private to you — your broker cannot see them."
      />

      <div className="mb-4">
        <Input
          id="synced-contact-search"
          name="synced_contact_search"
          placeholder="Search your contacts by name or number"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
        />
      </div>

      {actionError && <p className="mb-3 text-sm text-red-600">{actionError}</p>}

      {renderBody()}

      <Modal
        isOpen={Boolean(editing)}
        onClose={() => setEditing(null)}
        title="Edit contact"
      >
        <div className="space-y-4">
          <Input
            id="synced-contact-name"
            name="synced_contact_name"
            label="Name"
            value={draftName}
            onChange={(event) => setDraftName(event.target.value)}
          />
          <Input
            id="synced-contact-notes"
            name="synced_contact_notes"
            label="Your note"
            value={draftNotes}
            onChange={(event) => setDraftNotes(event.target.value)}
          />
          {/* The number is the contact's identity on the server, so it is shown
              and not edited: a changed number arrives with the next sync. */}
          <p className="text-xs text-gray-400">
            Phone number: {editing?.phone}. It updates from your phone on the next sync.
          </p>
        </div>
        <ModalFooter>
          <Button variant="outline" onClick={() => setEditing(null)}>
            Cancel
          </Button>
          <Button
            isLoading={saveMutation.isPending}
            onClick={() =>
              editing && saveMutation.mutate({ id: editing.id, name: draftName, notes: draftNotes })
            }
          >
            Save
          </Button>
        </ModalFooter>
      </Modal>
    </Card>
  );
}
